import { z } from 'zod';
import { ContentIdSchema, DescriptionSchema, DisplayNameSchema, RaritySchema } from './common.js';
import { ElementIdSchema, FeelingIdSchema } from './elements.js';
import { PaletteRoleSchema, PartSlotSchema } from './visuals.js';

const statValue = z.number().int().min(1).max(255);

export const BaseStatsSchema = z.strictObject({
  hp: statValue,
  attack: statValue,
  defense: statValue,
  speed: statValue,
});
export type BaseStats = z.infer<typeof BaseStatsSchema>;

/**
 * Public, level-based evolutions (design doc §8). Several at one level are a
 * branch (#32): the first listed is the default form. Branch odds, rare
 * conditions and secret branches are server-only and never live here.
 */
export const EvolutionSchema = z.strictObject({
  into: ContentIdSchema,
  level: z.number().int().min(2).max(100),
});
export type Evolution = z.infer<typeof EvolutionSchema>;

/** How a squishy stands (ART_BIBLE §1.2), for battle animation. */
export const PoseSchema = z.enum(['sit', 'stand', 'upright', 'slither', 'hover']);
export type Pose = z.infer<typeof PoseSchema>;

/** Rarity material tiers (ART_BIBLE §1.4). */
export const FinishSchema = z.enum(['vinyl', 'sparkle', 'iridescent', 'shimmer']);
export type Finish = z.infer<typeof FinishSchema>;

/** What glows (ART_BIBLE §1.4): the whole squishy, or only its `accent` parts. */
export const GlowSchema = z.enum(['body', 'accent']);
export type Glow = z.infer<typeof GlowSchema>;

/** A floater's glyph: each kind has one flat shape builder in the client. */
export const FloaterGlyphSchema = z.enum(['music-note']);
export type FloaterGlyph = z.infer<typeof FloaterGlyphSchema>;

/**
 * A flat glyph that floats beside a squishy, always facing the camera (a
 * music note; later a sleepy Zzz or a sparkle). It moves, hops and goes away
 * with the squishy, never casts a shadow, and bobs gently unless motion is
 * reduced. Off unless set. Proportions are of the body's height (`size`,
 * `above`, `bob.height`) or width (`side`), at the species' size.
 */
export const FloaterSchema = z.strictObject({
  glyph: FloaterGlyphSchema,
  /** The glyph's height. */
  size: z.number().min(0.1).max(0.8),
  /** The gap from the squishy's top to the glyph's bottom (negative tucks it down beside the top). */
  above: z.number().min(-0.5).max(0.8),
  /** From the middle to the glyph's centre: positive is the viewer's right when it faces you. */
  side: z.number().min(-1).max(1),
  /** Palette role it's drawn in; its outline is a deep shade of the same colour. */
  color: PaletteRoleSchema,
  /** Up-and-down bob: its height, and bobs per second (0 holds it still). */
  bob: z.strictObject({
    height: z.number().min(0).max(0.2),
    speed: z.number().min(0).max(2),
  }),
});
export type Floater = z.infer<typeof FloaterSchema>;

export const HexColorSchema = z
  .string()
  .regex(/^#[0-9a-f]{6}$/, 'Expected a colour like "#ffaa00"');

/**
 * Procedural vinyl-toy parameters (design doc §19). `body` and `parts` are
 * ids from the registry (`BODIES`, `PARTS`); `checkGameData` checks them, and
 * every squishy needs a part in the `eyes` slot. The palette is the
 * `primary`, `secondary`, `accent` and `detail` colours in that order (see
 * `PaletteRoleSchema`). Each squishy's small variations are seeded from its
 * instance id on the client, so this is all the species decides.
 */
export const SpeciesVisualSchema = z.strictObject({
  body: ContentIdSchema,
  palette: z.array(HexColorSchema).min(1).max(4),
  parts: z.array(ContentIdSchema).max(12),
  /** Overall scale; an evolution is ×1.2–1.4 its base (ART_BIBLE §1.3). Defaults to 1. */
  size: z.number().min(0.5).max(2).optional(),
  /** Face ink (eyes, brows, mouth); defaults to `artRules.defaultInk`. Chosen for contrast with the body. */
  ink: HexColorSchema.optional(),
  /** Rarity material tier (ART_BIBLE §1.4); must match `artRules.finishByRarity`. Defaults to vinyl. */
  finish: FinishSchema.optional(),
  /** Lit from inside: the whole squishy, or only its `accent` parts (flames). */
  glow: GlowSchema.optional(),
  /**
   * A separate head (ART_BIBLE §1.2): a registry body sitting on the torso,
   * `size` times the torso's height, moved `forward` and `up` (fractions of
   * the torso's height). Face, ears, horns, crown and mane go on it.
   */
  head: z
    .strictObject({
      body: ContentIdSchema,
      size: z.number().min(0.3).max(2),
      forward: z.number().min(-0.6).max(0.8),
      up: z.number().min(0).max(1.4),
    })
    .optional(),
  /** Lifts the torso off the ground (a fraction of its height); legs grow to reach the ground, or it hovers. */
  stance: z.number().min(0).max(1.2).optional(),
  /** How it stands, for battle animation (the battle-feel lane). */
  pose: PoseSchema.optional(),
  /** The slot a move animation swings, stomps or flares (tail, arms, horns…). */
  attackPart: PartSlotSchema.optional(),
  /** A camera-facing glyph floating beside it (a music note). Off unless set. */
  floater: FloaterSchema.optional(),
  /**
   * Keeps the sparkle tier's flecks (ART_BIBLE §1.4) off the front of its
   * body, where its face is, and off its underside, so they never read as a
   * nose or a stray mark. Off unless set.
   */
  sparkleOffFace: z.boolean().optional(),
});
export type SpeciesVisual = z.infer<typeof SpeciesVisualSchema>;

export const HabitatPreferencesSchema = z.strictObject({
  elements: z.array(ElementIdSchema),
  feelings: z.array(FeelingIdSchema),
});
export type HabitatPreferences = z.infer<typeof HabitatPreferencesSchema>;

/**
 * A squishy species (design doc §4). Spawn rules are secret, so they live in
 * the server-only spawn tables, not on the species.
 */
export const SpeciesSchema = z.strictObject({
  id: ContentIdSchema,
  name: DisplayNameSchema,
  description: DescriptionSchema,
  element: ElementIdSchema,
  feeling: FeelingIdSchema,
  rarity: RaritySchema,
  season: ContentIdSchema.optional(),
  baseStats: BaseStatsSchema,
  /** Move ids; each species has 2–4 moves (design doc §6). */
  moves: z.array(ContentIdSchema).min(2).max(4),
  evolutions: z.array(EvolutionSchema),
  visual: SpeciesVisualSchema,
  habitatPreferences: HabitatPreferencesSchema,
});
export type Species = z.infer<typeof SpeciesSchema>;
